import { HttpResult, loadingAsyncState } from 'widgets-for-react'
import { WaitingFor } from '../blocks/block/waitingTimesBlock/waitingTimesBlockState'
import {
  DepartmentPage,
  SpaarnePage,
  HomePage,
  EventPage,
  NewsPage,
  LocationPage,
  SpecialistPage,
  SubdepartmentPage,
  WaitingTimesPage,
  ResearchPage,
  CentrumPage,
  Page,
  SubcentraPage,
  FriendPage,
  FolderPage,
  FolderContent,
  DossierPage,
} from '../cmsPageState'
import {
  transformHomePageData,
  transformSpaarnePageData,
  transformDepartmentPageData,
  transformSubDepartmentPageData,
  transformNewsPageData,
  transformLocationPageData,
  transformSpecialistPageData,
  transformEventPageData,
  transformWaitingTimesPageData,
  transformResearchPageData,
  transformCentrumPageData,
  transformSharePointPageData,
  transformSubCentraPageData,
  transformFriendPageData,
  transformFolderPageData,
  transformDossierPageData,
} from './transformers/pageDataToPage'
import {
  CentrumPageData,
  DepartmentPageData,
  EventPageData,
  SpaarnePageData,
  HomePageData,
  LocationPageData,
  NewsPageData,
  ResearchPageData,
  SpecialistPageData,
  SubdepartmentPageData,
  WaitingTimesPageData,
  SharepointPageData,
  SubcentraPageData,
  FriendPageData,
  FolderPageData,
  DossierPageData,
} from './types/pageData'
import { DossierHighlightResult, DossierNewsResult, PagesClient, SearchableAgendaEventPage, SearchableNewsPage, SearchablePatientFolder } from '../../../backendApi/generated'

const fallbackMetaTitle = 'Spaarne Gasthuis'

const pageClient = new PagesClient(window.location.origin)

const getMetaTitle = (page: { HeaderTitle?: string; MetaTitle?: string; Title?: string }): string => {
  const pageTitle = page.MetaTitle || page.HeaderTitle || page.Title || fallbackMetaTitle
  if (pageTitle.endsWith(`| ${fallbackMetaTitle}`)) {
    return pageTitle
  } else {
    return `${pageTitle} | ${fallbackMetaTitle}`
  }
}

export const getHomePage = async (language: string): Promise<HttpResult<HomePage>> => {
  const res = await fetch(`/api/v1/pages/homepage/${language}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: HomePageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformHomePageData(data),
  }
}

export const getSpaarnePage = async (
  language: string,
  grandParentSlug: string | undefined,
  parentSlug: string | undefined,
  slug: string
): Promise<HttpResult<SpaarnePage>> => {
  const res = await fetch(
    `/api/v1/pages/spaarne?languageCode=${language}${grandParentSlug ? `&grandParentSlug=${grandParentSlug}` : ''}${
      parentSlug ? `&parentSlug=${parentSlug}` : ''
    }&slug=${slug}`
  )

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SpaarnePageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformSpaarnePageData(data),
  }
}

export const getPreview = async (slug: string, token: string): Promise<HttpResult<Page>> => {
  const res = await fetch(`/api/v1/sharepoint/preview/${slug.toLowerCase()}?token=${token}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SharepointPageData = await res.json()

  return {
    kind: 'result',
    status: 200,
    value: transformSharePointPageData(data),
  }
}

export const getFriendPage = async (slug: string): Promise<HttpResult<FriendPage>> => {
  const res = slug === '' ? await fetch(`/api/v1/pages/friend-page` ) : await fetch(`/api/v1/pages/friend-page/${slug.toLowerCase()} ` )

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: FriendPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformFriendPageData(data),
  }
}

export const getDepartmentOrCentrumPage = async (slug: string): Promise<HttpResult<DepartmentPage | CentrumPage>> => {
  const res = await fetch(`/api/v1/pages/department-or-centrum/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: DepartmentPageData | CentrumPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: data.ContentType === 'Afdeling pagina' ? transformDepartmentPageData(data) : transformCentrumPageData(data),
  }
}

export const getSubDepartmentOrDepartmentOrSubcentrumOrSpaarne = async (
  departmentOrCentrumSlug: string,
  slug: string
): Promise<HttpResult<SubdepartmentPage | DepartmentPage | SubcentraPage | SpaarnePage>> => {
  const res = await fetch(
    `/api/v1/pages/subdepartment-or-department-or-subcentra-or-spaarne?departmentOrCentrumSlug=${departmentOrCentrumSlug.toLowerCase()}&slug=${slug.toLowerCase()}`
  )

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SubdepartmentPageData | DepartmentPageData | SubcentraPageData | SpaarnePageData = await res.json()

  document.title = getMetaTitle(data)
  return {
    kind: 'result',
    status: 200,
    value:
      data.ContentType === 'Afdeling pagina'
        ? transformDepartmentPageData(data)
        : data.ContentType === 'Spaarne pagina'
        ? transformSpaarnePageData(data)
        : data.ContentType === 'Sub-centra pagina'
        ? transformSubCentraPageData(data)
        : transformSubDepartmentPageData(data),
  }
}

export const getSubDepartmentOrSpaarnePage = async (
  slug: string,
  departmentOrSpaarneSlug: string,
  centrumSlug: string
): Promise<HttpResult<SubdepartmentPage | SpaarnePage>> => {
  const res = await fetch(
    `/api/v1/pages/subdepartment-or-spaarne?centrumSlug=${centrumSlug}&departmentOrSpaarneSlug=${departmentOrSpaarneSlug}&slug=${slug.toLowerCase()}`
  )

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SubdepartmentPageData | SpaarnePageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value:
      data.ContentType === 'Sub-afdeling pagina'
        ? transformSubDepartmentPageData(data)
        : transformSpaarnePageData(data),
  }
}

export const getEventPage = async (slug: string): Promise<HttpResult<EventPage>> => {
  const res = await fetch(`/api/v1/pages/events/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: 404,
    }

  const data: EventPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformEventPageData(data),
  }
}

export const getNewsPage = async (slug: string): Promise<HttpResult<NewsPage>> => {
  const res = await fetch(`/api/v1/pages/news/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: NewsPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformNewsPageData(data),
  }
}

export const getLocationOrSpaarnePage = async (slug: string): Promise<HttpResult<LocationPage | SpaarnePage>> => {
  const res = await fetch(`/api/v1/pages/locationOrSpaarne/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: 404,
    }

  const data: LocationPageData | SpaarnePageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: data.ContentType == 'Locatie pagina' ? transformLocationPageData(data) : transformSpaarnePageData(data),
  }
}

export const getSpecialistPage = async (slug: string): Promise<HttpResult<SpecialistPage>> => {
  const res = await fetch(`/api/v1/pages/specialists/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SpecialistPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformSpecialistPageData(data),
  }
}

export const getWaitingTimesPage = async (
  departmentTitle?: string,
  centrumTitle?: string
): Promise<HttpResult<WaitingTimesPage>> => {
  const res = await fetch('/api/v1/pages/waiting-times')

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: WaitingTimesPageData = await res.json()

  document.title = getMetaTitle(data)

  const dep: WaitingFor | undefined = departmentTitle
    ? { forPage: 'department', title: departmentTitle }
    : centrumTitle
    ? { forPage: 'centrum', title: centrumTitle }
    : undefined

  return {
    kind: 'result',
    status: 200,
    value: transformWaitingTimesPageData(data, dep),
  }
}

export const getResearchPage = async (slug: string): Promise<HttpResult<ResearchPage>> => {
  const res = await fetch(`/api/v1/pages/research/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: ResearchPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformResearchPageData(data),
  }
}

export const getFolderPage = async (slug: string): Promise<HttpResult<FolderPage>> => {
  const res = await fetch(`/api/v1/pages/folderPage/${slug.toLowerCase()}`)

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: FolderPageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformFolderPageData(data),
  }
}

export const getDossierChildPage = async (dossierSlug: string, slug: string): Promise<HttpResult<SpaarnePage>> => {
  const res = await fetch(
    `/api/v1/pages/dossier-child?dossierSlug=${encodeURIComponent(dossierSlug.toLowerCase())}&slug=${encodeURIComponent(slug.toLowerCase())}`
  )

  if (!res.ok)
    return {
      kind: 'failed',
      status: res.status,
    }

  const data: SpaarnePageData = await res.json()

  document.title = getMetaTitle(data)

  return {
    kind: 'result',
    status: 200,
    value: transformSpaarnePageData(data),
  }
}

export const getDossierPage = async (slug: string): Promise<HttpResult<DossierPage>> => {
  const res = await pageClient.getDossierPage(slug.toLowerCase())
  document.title = getMetaTitle(res)
  return { kind: 'result', status: 200, value: transformDossierPageData(res) }
}

export const getFolderContent = async (folderId: number): Promise<HttpResult<SearchablePatientFolder>> => {
  const res = await pageClient.getFolderContent(folderId.toString())
  return { kind: 'result', status: 200, value: res }
}

export const getDossierHighlight = async (dossierPageId: number | undefined, dossierName: string, pageSize: number = 3): Promise<HttpResult<DossierHighlightResult>> => {
  const res = await pageClient.getDossierHighlight(dossierPageId, dossierName, pageSize)
  return { kind: 'result', status: 200, value: res }
}

export const getDossierNews = async (dossierName: string, page: number = 0, pageSize: number = 3): Promise<HttpResult<DossierNewsResult>> => {
  const res = await pageClient.getDossierNews(
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    dossierName,
    page,
    pageSize,
    false,
    false
  )
  return { kind: 'result', status: 200, value: res }
}

export const getDossierAgenda = async (dossierName: string, page: number = 0, pageSize: number = 3): Promise<HttpResult<SearchableAgendaEventPage[]>> => {
  const res = await pageClient.getFilteredEvents(
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    dossierName,
    page,
    pageSize
  )
  return { kind: 'result', status: 200, value: res }
}
